import * as React from 'react';

import NavigationBar, {
	PrimaryNavigation,
	SecondaryNavigation,
	UtilityNavigation,
} from '@duel/navigation-bar';

import type { intlShape } from 'react-intl';

import type { IOgmAiSavings } from '~/Slices/OGMAI/SuggestionsSlice';
import { Customer, LocalStorage, User } from '../../../helpers';
import route from '../../../helpers/Routes';
import { isDssiSiteType } from '../../../helpers/User';
import { key } from '../../../messages';
import Button from '../../DSX/Button';
import OGMLink from '../Link';

interface IDSXNavBarProps {
	className?: string;
	isHidden?: boolean;
	customerName?: string;
	onNotesLinkClicked: () => void;
	onRestrictionsLinkClicked: () => void;
	onMasterOrderGuidesLinkClicked: () => void;
	onUserAdminClicked: () => void;
	onOGMAIClicked: () => void;
	onGlobalSearchClicked: () => void;
	onGlobalActivityClicked: () => void;
	onBrandClicked: (event: React.MouseEvent<HTMLAnchorElement>) => void;
	onBrandClickedNoCustomer: (
		event: React.MouseEvent<HTMLAnchorElement>
	) => void;
	optimizationSavings: IOgmAiSavings;
	intl: intlShape;
	hideSignOutLink?: boolean;
}

export default class DSXNavBar extends React.PureComponent<IDSXNavBarProps> {
	public render() {
		const {
			isHidden,
			customerName,
			onNotesLinkClicked,
			onUserAdminClicked,
			onOGMAIClicked,
			onGlobalSearchClicked,
			onGlobalActivityClicked,
			onBrandClicked,
			onBrandClickedNoCustomer,
			hideSignOutLink,
		} = this.props;

		const isDssi = isDssiSiteType();

		const restrictionsHeading = this.props.intl.formatMessage({
			id: key((o) => o.containers.restrictionListing.heading),
		});

		// rendering logic for restrictions page

		let restrictionsLinkEl = null;

		if (User.isSuperUser()) {
			restrictionsLinkEl = (
				<OGMLink
					isHidden={!User.isSuperUser()}
					data-test-id="restrictions-link"
					to="/restrictions"
					onClick={this.props.onRestrictionsLinkClicked}
				>
					{restrictionsHeading}
				</OGMLink>
			);
		}

		// rendering logic for multiple customers
		let changeCustomerEl = null;

		if (LocalStorage.hasAccessToMultipleCustomers()) {
			changeCustomerEl = (
				<OGMLink to="/customers" data-test-id="customer-name">
					<i className="fa fa-user" /> {this.props.customerName} • Change
					Customer
				</OGMLink>
			);
		} else {
			changeCustomerEl = (
				<span data-test-id="customer-name">
					<i className="fa fa-user" /> {this.props.customerName}
				</span>
			);
		}

		// Nav Bar rendering logic between Sysco and DSSI
		const ogmAiEnabled =
			Customer.canManageAiSettings() && User.canViewAiFeatures();

		const ogmAISavings = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			currencyDisplay: 'symbol',
			minimumFractionDigits:
				this.props.optimizationSavings.newSavings >= 1000 ? 0 : 2,
			maximumFractionDigits:
				this.props.optimizationSavings.newSavings >= 1000 ? 0 : 2,
			notation: 'compact',
			compactDisplay: 'short',
		}).format(this.props.optimizationSavings.newSavings ?? 0);

		let navBar = null;
		if (!isDssi) {
			const primaryNav =
				customerName === 'null' ? (
					<PrimaryNavigation>
						<OGMLink to="/customers">Customers</OGMLink>
					</PrimaryNavigation>
				) : (
					<PrimaryNavigation>
						<OGMLink to="/" data-test-id="master-order-guide-link">
							Master Order Guides
						</OGMLink>
						{restrictionsLinkEl}
						<OGMLink
							to="/alerts"
							onClick={onUserAdminClicked}
							data-test-id="alerts-link"
						>
							Alerts
						</OGMLink>
						<OGMLink to="/reports" data-test-id="reports-link">
							Reports
						</OGMLink>
						<OGMLink
							to="/notes"
							onClick={onNotesLinkClicked}
							data-test-id="notes-link"
						>
							Notes
						</OGMLink>
						<OGMLink
							to="/search"
							data-test-id="global-search-link"
							onClick={onGlobalSearchClicked}
						>
							Search
						</OGMLink>
						<OGMLink
							to="/activity"
							data-test-id="global-activity-link"
							onClick={onGlobalActivityClicked}
						>
							Activity
						</OGMLink>
					</PrimaryNavigation>
				);
			const changeCustomer =
				customerName === 'null' ? (
					<OGMLink
						to="/customers"
						isHidden={!LocalStorage.hasAccessToMultipleCustomers()}
					>
						Change Customer
					</OGMLink>
				) : (
					changeCustomerEl
				);

			navBar = !isHidden && (
				<NavigationBar
					brandHref={customerName === 'null' ? '/customers' : '/'}
					onBrandClick={
						customerName === 'null' ? onBrandClickedNoCustomer : onBrandClicked
					}
					brandImage="/images/Sysco_Logo_Inverted.png"
					maxWidth="97rem"
				>
					{primaryNav}
					<UtilityNavigation>
						<OGMLink
							to="/invoiceSequences"
							isHidden={!User.isSyscoSiteType() || customerName === 'null'}
						>
							Sequence Charts
						</OGMLink>
						<OGMLink
							to="/selfservice/managecustomers"
							isHidden={!User.canManageCustomers()}
						>
							Manage Customers
						</OGMLink>
						<OGMLink
							to="/selfservice/customeritemnumbers"
							isHidden={customerName === 'null'}
						>
							Manage Customer Item Numbers
						</OGMLink>
						<OGMLink
							to="/customcategories"
							isHidden={
								!User.canManageCustomCategories() ||
								!Customer.customCategoriesEnabled()
							}
						>
							Manage Custom Categories
						</OGMLink>
						<OGMLink to="/admin/users" isHidden={!User.canEditUsers()}>
							Manage Users
						</OGMLink>
						{changeCustomer}
						{!hideSignOutLink && (
							<OGMLink href={route.logout()} data-test-id="sign-out-link">
								Sign Out
							</OGMLink>
						)}
					</UtilityNavigation>
				</NavigationBar>
			);
		} else {
			const primaryNav =
				customerName === 'null' ? (
					<PrimaryNavigation>
						<OGMLink to="/customers">Customers</OGMLink>
					</PrimaryNavigation>
				) : (
					<PrimaryNavigation>
						<OGMLink to="/" data-test-id="master-order-guide-link">
							Master Order Guides
						</OGMLink>
						{restrictionsLinkEl}
						<OGMLink
							to="/alerts"
							onClick={onUserAdminClicked}
							data-test-id="alerts-link"
						>
							Alerts
						</OGMLink>
						<OGMLink to="/reports" data-test-id="reports-link">
							Reports
						</OGMLink>
						<OGMLink
							to="/search"
							data-test-id="global-search-link"
							onClick={onGlobalSearchClicked}
						>
							Search
						</OGMLink>
						<OGMLink
							to="/activity"
							data-test-id="global-activity-link"
							onClick={onGlobalActivityClicked}
						>
							Activity
						</OGMLink>
					</PrimaryNavigation>
				);
			const changeCustomer =
				customerName === 'null' ? (
					<OGMLink
						to="/customers"
						isHidden={!LocalStorage.hasAccessToMultipleCustomers()}
						data-test-id="customer-name"
					>
						Change Customer
					</OGMLink>
				) : (
					changeCustomerEl
				);

			navBar = !isHidden && (
				<NavigationBar
					brandHref={customerName === 'null' ? '/customers' : '/'}
					onBrandClick={
						customerName === 'null' ? onBrandClickedNoCustomer : onBrandClicked
					}
					brandImage="/images/DSSI_OGM_3_Logo_Centered.svg"
					maxWidth="97rem"
				>
					{primaryNav}
					{ogmAiEnabled && customerName !== 'null' && (
						<SecondaryNavigation>
							<div className="ogm-ai-nav">
								<span className="ogm-ai-nav-svg">
									<svg
										width="75"
										viewBox="0 0 121 30"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M25.3352 13.9091C25.3352 16.7642 24.794 19.1932 23.7116 21.196C22.6378 23.1989 21.1719 24.7287 19.3139 25.7855C17.4645 26.8338 15.3849 27.358 13.0753 27.358C10.7486 27.358 8.66051 26.8295 6.81108 25.7727C4.96165 24.7159 3.5 23.1861 2.42614 21.1832C1.35227 19.1804 0.815341 16.7557 0.815341 13.9091C0.815341 11.054 1.35227 8.625 2.42614 6.62216C3.5 4.61932 4.96165 3.09375 6.81108 2.04545C8.66051 0.988636 10.7486 0.460226 13.0753 0.460226C15.3849 0.460226 17.4645 0.988636 19.3139 2.04545C21.1719 3.09375 22.6378 4.61932 23.7116 6.62216C24.794 8.625 25.3352 11.054 25.3352 13.9091ZM19.723 13.9091C19.723 12.0597 19.446 10.5 18.892 9.23011C18.3466 7.96023 17.5753 6.99716 16.5781 6.34091C15.581 5.68466 14.4134 5.35653 13.0753 5.35653C11.7372 5.35653 10.5696 5.68466 9.57244 6.34091C8.57528 6.99716 7.79972 7.96023 7.24574 9.23011C6.70028 10.5 6.42756 12.0597 6.42756 13.9091C6.42756 15.7585 6.70028 17.3182 7.24574 18.5881C7.79972 19.858 8.57528 20.821 9.57244 21.4773C10.5696 22.1335 11.7372 22.4616 13.0753 22.4616C14.4134 22.4616 15.581 22.1335 16.5781 21.4773C17.5753 20.821 18.3466 19.858 18.892 18.5881C19.446 17.3182 19.723 15.7585 19.723 13.9091ZM46.0045 9.28125C45.8255 8.65909 45.5741 8.10937 45.2503 7.6321C44.9264 7.14631 44.5301 6.73722 44.0613 6.40483C43.6011 6.06392 43.0727 5.80398 42.4761 5.625C41.888 5.44602 41.2361 5.35653 40.5201 5.35653C39.1821 5.35653 38.0059 5.68892 36.9917 6.35369C35.9861 7.01846 35.202 7.98579 34.6395 9.25568C34.077 10.517 33.7957 12.0597 33.7957 13.8835C33.7957 15.7074 34.0727 17.2585 34.6267 18.5369C35.1807 19.8153 35.9647 20.7912 36.979 21.4645C37.9932 22.1293 39.1906 22.4616 40.5713 22.4616C41.8241 22.4616 42.8937 22.2401 43.7801 21.7969C44.675 21.3452 45.3568 20.7102 45.8255 19.892C46.3028 19.0739 46.5415 18.1065 46.5415 16.9901L47.6665 17.1562H40.9165V12.9886H51.8724V16.2869C51.8724 18.5881 51.3866 20.5653 50.415 22.2188C49.4434 23.8636 48.1054 25.1335 46.4008 26.0284C44.6963 26.9148 42.7446 27.358 40.5457 27.358C38.0912 27.358 35.9349 26.8168 34.077 25.7344C32.219 24.6435 30.7701 23.0966 29.7304 21.0938C28.6991 19.0824 28.1835 16.696 28.1835 13.9347C28.1835 11.8125 28.4903 9.92045 29.104 8.25852C29.7261 6.58807 30.5954 5.1733 31.7119 4.0142C32.8284 2.85511 34.1281 1.97301 35.6111 1.3679C37.094 0.762783 38.7005 0.460226 40.4307 0.460226C41.9136 0.460226 43.2943 0.677556 44.5727 1.11222C45.8511 1.53835 46.9846 2.14347 47.9733 2.92756C48.9704 3.71165 49.7844 4.64489 50.415 5.72727C51.0457 6.80114 51.4505 7.98579 51.6295 9.28125H46.0045ZM55.2384 0.818181H62.0651L69.2754 18.4091H69.5822L76.7924 0.818181H83.6191V27H78.2498V9.95881H78.0325L71.2569 26.8722H67.6007L60.8251 9.89489H60.6078V27H55.2384V0.818181ZM90.4688 27.3324C89.6251 27.3324 88.9006 27.0341 88.2955 26.4375C87.6989 25.8324 87.4006 25.108 87.4006 24.2642C87.4006 23.429 87.6989 22.7131 88.2955 22.1165C88.9006 21.5199 89.6251 21.2216 90.4688 21.2216C91.287 21.2216 92.0029 21.5199 92.6165 22.1165C93.2302 22.7131 93.537 23.429 93.537 24.2642C93.537 24.8267 93.3921 25.3423 93.1023 25.8111C92.8211 26.2713 92.4504 26.642 91.9901 26.9233C91.5299 27.196 91.0228 27.3324 90.4688 27.3324Z"
											fill="black"
										/>
										<path
											d="M103.583 27.4602C102.338 27.4602 101.209 27.2259 100.195 26.7571C99.1807 26.2798 98.3753 25.5938 97.7787 24.6989C97.1821 23.7955 96.8838 22.7045 96.8838 21.4261C96.8838 20.3011 97.1054 19.3892 97.5486 18.6903C97.9918 17.983 98.5841 17.429 99.3256 17.0284C100.067 16.6278 100.885 16.3295 101.78 16.1335C102.684 15.929 103.591 15.767 104.503 15.6477C105.696 15.4943 106.664 15.3793 107.405 15.3026C108.155 15.2173 108.701 15.0767 109.042 14.8807C109.391 14.6847 109.566 14.3437 109.566 13.858V13.7557C109.566 12.4943 109.22 11.5142 108.53 10.8153C107.848 10.1165 106.813 9.76705 105.424 9.76705C103.983 9.76705 102.854 10.0824 102.036 10.7131C101.218 11.3437 100.642 12.017 100.31 12.733L97.4463 11.7102C97.9577 10.517 98.6395 9.58807 99.4918 8.9233C100.353 8.25 101.29 7.78125 102.304 7.51705C103.327 7.24432 104.333 7.10795 105.321 7.10795C105.952 7.10795 106.676 7.18466 107.495 7.33807C108.321 7.48295 109.118 7.78551 109.885 8.24574C110.661 8.70597 111.304 9.40057 111.816 10.3295C112.327 11.2585 112.583 12.5028 112.583 14.0625V27H109.566V24.3409H109.412C109.208 24.767 108.867 25.223 108.39 25.7088C107.912 26.1946 107.277 26.608 106.485 26.9489C105.692 27.2898 104.725 27.4602 103.583 27.4602ZM104.043 24.75C105.236 24.75 106.242 24.5156 107.06 24.0469C107.887 23.5781 108.509 22.973 108.926 22.2315C109.353 21.4901 109.566 20.7102 109.566 19.892V17.1307C109.438 17.2841 109.157 17.4247 108.722 17.5526C108.296 17.6719 107.801 17.7784 107.239 17.8722C106.685 17.9574 106.144 18.0341 105.615 18.1023C105.095 18.1619 104.674 18.2131 104.35 18.2557C103.566 18.358 102.833 18.5241 102.151 18.7543C101.478 18.9759 100.932 19.3125 100.515 19.7642C100.105 20.2074 99.9009 20.8125 99.9009 21.5795C99.9009 22.6278 100.289 23.4205 101.064 23.9574C101.848 24.4858 102.841 24.75 104.043 24.75ZM117.297 27V7.36364H120.315V27H117.297ZM118.832 4.09091C118.243 4.09091 117.736 3.89062 117.31 3.49006C116.893 3.08949 116.684 2.60795 116.684 2.04545C116.684 1.48295 116.893 1.00142 117.31 0.600851C117.736 0.200284 118.243 0 118.832 0C119.42 0 119.922 0.200284 120.34 0.600851C120.766 1.00142 120.979 1.48295 120.979 2.04545C120.979 2.60795 120.766 3.08949 120.34 3.49006C119.922 3.89062 119.42 4.09091 118.832 4.09091Z"
											fill="#0065BD"
										/>
									</svg>
								</span>

								<Button
									className="ogm-ai-nav-button"
									style="primary"
									onClick={onOGMAIClicked}
									value={ogmAISavings}
									isLoading={this.props.optimizationSavings.isSavingsPending}
									dsxIcon="lightbulb-solid-glowing"
								/>
							</div>
						</SecondaryNavigation>
					)}
					<UtilityNavigation>
						<OGMLink
							to="/selfservice/managecustomers"
							isHidden={!User.canManageCustomers()}
						>
							Manage Customers
						</OGMLink>
						<OGMLink to="/admin/users" isHidden={!User.canEditUsers()}>
							Manage Users
						</OGMLink>
						{changeCustomer}
						{!hideSignOutLink && (
							<OGMLink href={route.logout()} data-test-id="sign-out-link">
								Sign Out
							</OGMLink>
						)}
					</UtilityNavigation>
				</NavigationBar>
			);
		}

		return navBar;
	}
}
